import { useEmojiStore } from 'src/stores/emoji.js'
import { useI18nStore } from 'src/stores/i18n.js'
import { useInstanceStore } from 'src/stores/instance.js'
import { useInterfaceStore } from 'src/stores/interface.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useOAuthStore } from 'src/stores/oauth.js'
import { useStreamingStore } from 'src/stores/streaming.js'
import { useUsersStore } from 'src/stores/users.js'

import { WSConnectionStatus } from 'src/api/websocket.js'

// Settings that do something when they change, however they change: the
// settings page, a restored backup, another device. Each effect names the
// store actions that can change it and runs after them.

// a store action setting this path (a synced one as simple.<key>)
const sets =
  (path) =>
  ({ name, args }) =>
    (name === 'set' || name === 'setPreference') && args[0]?.path === path

const setLanguage = ({ args }) => {
  const language = args[0].value
  useI18nStore().setLanguage(language)
  // names in the new language, if the emoji are loaded already
  if (useEmojiStore().unicodeEmojiLoading) {
    useEmojiStore().loadUnicodeEmojiData(language)
  }
}

// only for the logged-in (there is no stream without a token), and only
// when it changes: an imported settings file sets every value again
const setStreaming = ({ args }) => {
  if (!useOAuthStore().token) return
  const streaming = useStreamingStore()
  const wanted = !!args[0].value
  // between sockets (a retry pending, a check after it gave up) it runs too
  const running = ![null, WSConnectionStatus.CLOSED].includes(streaming.state)
  if (wanted === running) return
  if (wanted) streaming.initSocket()
  else streaming.stopSocket()
}

// Push follows the setting, the permission, the login and the server's
// key, which may arrive after all of these. Logging out cancels push
// itself, while its token still works.
const syncPush = () => {
  const ui = useInterfaceStore()
  if (!useInstanceStore().vapidPublicKey) return
  // at boot the key comes before the login, and the permission is asked
  // at login: nothing to decide until both are known
  if (!useUsersStore().loggedIn || ui.notificationPermission == null) return
  const wanted =
    ui.notificationPermission === 'granted' &&
    useMergedConfigStore().mergedConfig.webPushNotifications
  return wanted
    ? ui.registerPushNotifications()
    : ui.unregisterPushNotifications()
}

// store id => [which actions, what to run after them]
const EFFECTS = {
  sync_config: [
    [sets('simple.interfaceLanguage'), setLanguage],
    [sets('simple.webPushNotifications'), syncPush],
  ],
  local_config: [
    [sets('useStreamingApi'), setStreaming],
    [sets('webPushNotifications'), syncPush],
  ],
  instance: [[sets('vapidPublicKey'), syncPush]],
  interface: [
    [
      ({ name }) => ['setNotificationPermission', 'onLogin'].includes(name),
      syncPush,
    ],
  ],
}

export const piniaSettingEffectsPlugin = ({ store }) => {
  const effects = EFFECTS[store.$id]
  if (!effects) return
  store.$onAction((action) => {
    for (const [applies, run] of effects) {
      if (applies(action)) action.after(() => run(action))
    }
  })
}
